import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, withModifiers as _withModifiers, vModelText as _vModelText } from "vue"

const _hoisted_1 = { class: "add-employee" }
const _hoisted_2 = { class: "card mb-4" }
const _hoisted_3 = { class: "card-body" }
const _hoisted_4 = { class: "mb-3" }
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "mb-3" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["value"]
const _hoisted_9 = {
  key: 0,
  class: "text-muted"
}
const _hoisted_10 = {
  key: 1,
  class: "mt-2"
}
const _hoisted_11 = { class: "mb-2" }
const _hoisted_12 = { class: "selected-employees" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "d-flex justify-content-end gap-2" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = {
  key: 0,
  class: "spinner-border spinner-border-sm me-1",
  role: "status"
}
const _hoisted_17 = { class: "card" }
const _hoisted_18 = { class: "card-header d-flex justify-content-between align-items-center" }
const _hoisted_19 = { class: "input-group w-auto" }
const _hoisted_20 = { class: "card-body" }
const _hoisted_21 = { class: "table-responsive" }
const _hoisted_22 = { class: "table table-hover" }
const _hoisted_23 = ["onClick", "disabled"]
const _hoisted_24 = {
  key: 0,
  class: "spinner-border spinner-border-sm",
  role: "status"
}
const _hoisted_25 = {
  key: 1,
  class: "bi bi-trash"
}
const _hoisted_26 = {
  key: 0,
  class: "text-center my-4"
}
const _hoisted_27 = {
  key: 1,
  class: "text-center my-4"
}

import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Swal from 'sweetalert2';

interface Designation {
    _id: string;
    name: string;
}

interface Employee {
    _id: string;
    employeeName: string;
    employeeCode: string;
    employeeType: string;
    site: any | null;
    designation?: {
        _id: string;
        name: string;
    };
    permanentAddress?: {
        village?: string;
        postOffice?: string;
        policeStation?: string;
        district?: string;
        state?: string;
        pin?: string;
        phoneNumber?: string;
        alternatePhoneNumber?: string;
        alternatePhoneRelation?: string;
        _id?: string;
    };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'add-employee',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const siteId = route.params.siteId as string;
const baseUrl = process.env.VUE_APP_BASE_URL;

const formData = ref({
    name: '',
});

const employees = ref<Employee[]>([]);
const isLoading = ref(false);
const searchQuery = ref('');
const searchResults = ref<Employee[]>([]);
const selectedEmployees = ref<Employee[]>([]);
const removingEmployeeId = ref<string | null>(null);
const designations = ref<Designation[]>([]);
const selectedDesignation = ref<Designation | null>(null);
const allEmployees = ref<Employee[]>([]);
const isAddingEmployees = ref(false);

// Get unique designations from employees
const fetchEmployeesAndDesignations = async () => {
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}api/joining/employees`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        const result = await response.json();
        if (result.success) {
            allEmployees.value = result.data;
            // Extract unique designations from employees
            const uniqueDesignations = new Map();
            result.data.forEach((emp: Employee) => {
                if (emp.designation) {
                    uniqueDesignations.set(emp.designation._id, emp.designation);
                }
            });
            designations.value = Array.from(uniqueDesignations.values());
        }
    } catch (error) {
        console.error('Error fetching employees:', error);
    }
};

// Computed property for available employees based on designation
const availableEmployees = computed(() => {
    if (!selectedDesignation.value) return [];
    return allEmployees.value.filter(emp => 
        emp.designation?._id === selectedDesignation.value?._id && !emp.site
    );
});

// Handle designation change
const handleDesignationChange = () => {
    selectedEmployees.value = [];
};

// Remove employee from selection
const removeFromSelection = (employee: Employee) => {
    selectedEmployees.value = selectedEmployees.value.filter(emp => emp._id !== employee._id);
};

// Update handleSubmit to handle multiple employees
const handleSubmit = async () => {
    if (selectedEmployees.value.length === 0) return;
    
    isAddingEmployees.value = true;
    const errors: string[] = [];
    
    try {
        // Add employees one by one
        for (const employee of selectedEmployees.value) {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${baseUrl}api/sites/assign/employee/${siteId}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        employeeIds: employee._id
                    })
                });
                const result = await response.json();
                
                if (!result.success) {
                    errors.push(`Failed to add ${employee.employeeName}: ${result.message}`);
                }
            } catch (error) {
                errors.push(`Failed to add ${employee.employeeName}: Network error`);
            }
        }
        
        await fetchEmployees();
        await fetchEmployeesAndDesignations();
        
        // Reset form
        selectedEmployees.value = [];
        selectedDesignation.value = null;
        
        if (errors.length > 0) {
            Swal.fire({
                title: 'Partial Success',
                html: `Some employees could not be added:<br>${errors.join('<br>')}`,
                icon: 'warning'
            });
        } else {
            Swal.fire({
                title: 'Success!',
                text: 'All employees added successfully',
                icon: 'success'
            });
        }
    } catch (error) {
        console.error('Error adding employees:', error);
        Swal.fire({
            title: 'Error!',
            text: 'Failed to add employees',
            icon: 'error'
        });
    } finally {
        isAddingEmployees.value = false;
    }
};

// Filter employees based on search
const filteredEmployees = computed(() => {
    return employees.value.filter(employee => 
        employee.employeeName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        employee.employeeCode.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        employee.designation?.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        employee.permanentAddress?.phoneNumber?.includes(searchQuery.value) || false
    );
});

// Fetch employees list
const fetchEmployees = async () => {
    isLoading.value = true;
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}api/sites/get/employee/${siteId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        
        const result = await response.json();
        
        if (result.success && Array.isArray(result.data)) {
            employees.value = result.data;
        } else {
            console.error('Invalid API response format:', result);
            employees.value = [];
        }
    } catch (error) {
        console.error('Error fetching employees:', error);
        employees.value = [];
    } finally {
        isLoading.value = false;
    }
};

// Remove employee with loading state
const removeEmployeeWithLoading = async (employeeId: string) => {
    const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You want to remove this employee from the site?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, remove!',
        cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;
    
    removingEmployeeId.value = employeeId;
    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${baseUrl}api/sites/remove/employee/${siteId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                employeeIds: employeeId
            })
        });
        
        const apiResult = await response.json();
        
        if (apiResult.success) {
            await fetchEmployees();
            Swal.fire(
                'Removed!',
                'Employee has been removed from the site.',
                'success'
            );
        } else {
            Swal.fire(
                'Error!',
                apiResult.message || 'Failed to remove employee',
                'error'
            );
        }
    } catch (error) {
        console.error('Error removing employee:', error);
        Swal.fire(
            'Error!',
            'Failed to remove employee from site',
            'error'
        );
    } finally {
        removingEmployeeId.value = null;
    }
};

// Initialize component
onMounted(() => {
    fetchEmployees();
    fetchEmployeesAndDesignations();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[9] || (_cache[9] = _createElementVNode("div", { class: "card-header" }, [
        _createElementVNode("h3", null, "Add Employee")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("form", {
          onSubmit: _withModifiers(handleSubmit, ["prevent"])
        }, [
          _createElementVNode("div", _hoisted_4, [
            _cache[5] || (_cache[5] = _createElementVNode("label", { class: "form-label" }, "Designation", -1)),
            _withDirectives(_createElementVNode("select", {
              class: "form-select",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedDesignation).value = $event)),
              onChange: handleDesignationChange,
              required: ""
            }, [
              _cache[4] || (_cache[4] = _createElementVNode("option", { value: "" }, "Select Designation", -1)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(designations.value, (designation) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: designation._id,
                  value: designation
                }, _toDisplayString(designation.name), 9, _hoisted_5))
              }), 128))
            ], 544), [
              [_vModelSelect, selectedDesignation.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _cache[6] || (_cache[6] = _createElementVNode("label", { class: "form-label" }, "Employees", -1)),
            _withDirectives(_createElementVNode("select", {
              class: "form-select",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedEmployees).value = $event)),
              multiple: "",
              size: "5",
              disabled: !selectedDesignation.value,
              required: ""
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableEmployees.value, (employee) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: employee._id,
                  value: employee
                }, _toDisplayString(employee.employeeName) + " (" + _toDisplayString(employee.employeeCode) + ") ", 9, _hoisted_8))
              }), 128))
            ], 8, _hoisted_7), [
              [_vModelSelect, selectedEmployees.value]
            ]),
            _cache[7] || (_cache[7] = _createElementVNode("small", { class: "text-muted d-block mt-1" }, "Hold Ctrl/Cmd to select multiple employees", -1)),
            (!selectedDesignation.value)
              ? (_openBlock(), _createElementBlock("small", _hoisted_9, "Please select a designation first"))
              : _createCommentVNode("", true),
            (selectedEmployees.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _createElementVNode("p", _hoisted_11, "Selected Employees (" + _toDisplayString(selectedEmployees.value.length) + "):", 1),
                  _createElementVNode("div", _hoisted_12, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedEmployees.value, (emp) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: emp._id,
                        class: "selected-employee"
                      }, [
                        _createTextVNode(_toDisplayString(emp.employeeName) + " ", 1),
                        _createElementVNode("button", {
                          type: "button",
                          class: "btn-close ms-2",
                          onClick: ($event: any) => (removeFromSelection(emp)),
                          "aria-label": "Remove"
                        }, null, 8, _hoisted_13)
                      ]))
                    }), 128))
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_14, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-secondary",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(router).back()))
            }, " Cancel "),
            _createElementVNode("button", {
              type: "submit",
              class: "btn btn-primary",
              disabled: selectedEmployees.value.length === 0 || isAddingEmployees.value
            }, [
              (isAddingEmployees.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_16, _cache[8] || (_cache[8] = [
                    _createElementVNode("span", { class: "visually-hidden" }, "Loading...", -1)
                  ])))
                : _createCommentVNode("", true),
              _createTextVNode(" Add Employees (" + _toDisplayString(selectedEmployees.value.length) + ") ", 1)
            ], 8, _hoisted_15)
          ])
        ], 32)
      ])
    ]),
    _createElementVNode("div", _hoisted_17, [
      _createElementVNode("div", _hoisted_18, [
        _cache[10] || (_cache[10] = _createElementVNode("h3", null, "Existing Employees", -1)),
        _createElementVNode("div", _hoisted_19, [
          _withDirectives(_createElementVNode("input", {
            type: "text",
            class: "form-control",
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((searchQuery).value = $event)),
            placeholder: "Search employees..."
          }, null, 512), [
            [_vModelText, searchQuery.value]
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_20, [
        _createElementVNode("div", _hoisted_21, [
          _createElementVNode("table", _hoisted_22, [
            _cache[13] || (_cache[13] = _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", null, "Employee Code"),
                _createElementVNode("th", null, "Name"),
                _createElementVNode("th", null, "Designation"),
                _createElementVNode("th", null, "Phone Number"),
                _createElementVNode("th", null, "Actions")
              ])
            ], -1)),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredEmployees.value, (employee) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: employee._id
                }, [
                  _createElementVNode("td", null, _toDisplayString(employee.employeeCode), 1),
                  _createElementVNode("td", null, _toDisplayString(employee.employeeName), 1),
                  _createElementVNode("td", null, _toDisplayString(employee.designation?.name || 'N/A'), 1),
                  _createElementVNode("td", null, _toDisplayString(employee.permanentAddress?.phoneNumber || 'N/A'), 1),
                  _createElementVNode("td", null, [
                    _createElementVNode("button", {
                      class: "btn btn-sm btn-danger d-flex align-items-center gap-1",
                      onClick: ($event: any) => (removeEmployeeWithLoading(employee._id)),
                      disabled: removingEmployeeId.value === employee._id
                    }, [
                      (removingEmployeeId.value === employee._id)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_24, _cache[11] || (_cache[11] = [
                            _createElementVNode("span", { class: "visually-hidden" }, "Loading...", -1)
                          ])))
                        : (_openBlock(), _createElementBlock("i", _hoisted_25)),
                      _cache[12] || (_cache[12] = _createTextVNode(" Remove "))
                    ], 8, _hoisted_23)
                  ])
                ]))
              }), 128))
            ])
          ])
        ]),
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_26, _cache[14] || (_cache[14] = [
              _createElementVNode("div", {
                class: "spinner-border text-primary",
                role: "status"
              }, [
                _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
              ], -1)
            ])))
          : _createCommentVNode("", true),
        (!isLoading.value && employees.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_27, _cache[15] || (_cache[15] = [
              _createElementVNode("p", { class: "text-muted" }, "No employees found", -1)
            ])))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})